import { useLogout } from "@shared/hooks/useLogout";
import { useWorkspacesStore, Workspace } from "@shared/states/workspacesStore";
import NotificationIcon from "@shared/svg/notification.svg?react";
import AppVersion from "@shared/ui/AppVersion";
import { NotificationCounter, COUNTER_SIZES } from "@shared/ui/Indicators";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarSeparator,
  useSidebar,
} from "@shared/ui/SidebarNavigation";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";
import {
  WorkspaceSwitcher,
  type WorkspaceItem,
  type BookDemoFormData,
} from "@shared/ui/WorkspaceSwitcher";
import React, { useCallback, useEffect } from "react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { useRoutes } from "./hooks/useRoutes";

import useAuth from "~/components/general/_use-auth";
import useNotification from "~/hooks/_useNotification";
import { useBookDemo } from "~/services/bookDemo";
import type { UserType } from "~/types/user";
import { useGetAppConfigValue } from "~/utils/appFeatures";

const Navigation = () => {
  const { user } = useAuth() as { user: UserType };
  const { state } = useSidebar();
  const { logout } = useLogout();
  const { messages } = useIntl();
  const { submitBookDemo } = useBookDemo();
  const isCollapsed = state === "collapsed";
  const navigate = useNavigate();

  const basePath = "/app";
  const workspaces: Workspace[] = useWorkspacesStore((s) => s.workspaces);

  // Derive currentSpace once; use its stable id as effect dep to avoid
  // re-running on every render when setWorkspaces rebuilds the array with
  // new object references but the same underlying workspace.
  const currentSpace = workspaces.find((ws) => ws.type === "OEM");

  const hasIntercomAppId = Boolean(
    (
      import.meta as ImportMeta & {
        env: { VITE_APP_INTERCOM_APP_ID: number };
      }
    ).env.VITE_APP_INTERCOM_APP_ID,
  );

  const appConfig = useGetAppConfigValue();

  const { contentRouteSections, footerRoutes } = useRoutes(
    basePath,
    hasIntercomAppId,
  );
  const { unreadLabels } = useNotification();

  const [currentWorkspace, setCurrentWorkspace] =
    React.useState<WorkspaceItem | null>(currentSpace);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    () => setCurrentWorkspace(currentSpace ?? null),
    [currentSpace?.id],
  );

  const msg = messages.login?.["bookDemo"] as Record<string, string>;

  const onWorkspaceChange = (workspace: WorkspaceItem) => {
    setCurrentWorkspace(workspace);
    const url =
      workspace.type === "OEM"
        ? "/app/assets"
        : `/app/workspace/${workspace.id}/`;
    navigate(url);
  };

  const handleBookDemo = useCallback(
    async (data: BookDemoFormData): Promise<boolean> => {
      try {
        const success = await submitBookDemo(data);
        if (success) {
          toast(
            <Toast
              message={msg?.["successToast"] as string}
              type={TOAST_TYPES.SUCCESS}
            />,
            { closeButton: false },
          );
          return true;
        } else {
          toast(
            <Toast
              message={msg?.["errorToast"] as string}
              type={TOAST_TYPES.ERROR}
            />,
            { closeButton: false },
          );
          return false;
        }
      } catch {
        toast(
          <Toast
            message={
              (messages?.["common"]?.["errors"]?.[
                "somethingWrong"
              ] as string) || "Something went wrong"
            }
            type={TOAST_TYPES.ERROR}
          />,
          { closeButton: false },
        );
        return false;
      }
    },
    [submitBookDemo, msg, messages],
  );

  return (
    <Sidebar side="left" variant="default" collapsible="icon">
      <SidebarHeader>
        <WorkspaceSwitcher
          currentWorkspace={currentWorkspace}
          workspaces={workspaces}
          userEmail={user?.email}
          isCollapsed={isCollapsed}
          onWorkspaceChange={onWorkspaceChange}
          onAddWorkspace={handleBookDemo}
          onLogout={logout}
        />
      </SidebarHeader>
      <div className="relative flex-[1_0_0] min-h-0 overflow-y-auto no-scrollbar">
        <SidebarContent className="!overflow-visible h-auto">
          {contentRouteSections.map((section, sectionIndex) => (
            <React.Fragment key={sectionIndex}>
              {sectionIndex > 0 && <SidebarSeparator />}
              <SidebarGroup>
                <SidebarMenu>
                  {section.map((route) =>
                    route.disableIf?.(user, appConfig?.features) ? null : (
                      <SidebarMenuItem key={route.to}>
                        <SidebarMenuButton
                          to={route.to}
                          icon={route.icon}
                          activeIcon={route.activeIcon}
                          label={route.label}
                          showTooltip
                          isActive={route.isActive}
                        >
                          <div className="ml-auto relative">
                            {unreadLabels[route.notificationKey] > 0 && (
                              <>
                                {isCollapsed ? (
                                  <NotificationIcon className="absolute right-0 top-0 w-xs h-xs -translate-x-2 translate-y-2" />
                                ) : (
                                  <NotificationCounter
                                    count={unreadLabels[route.notificationKey]}
                                    textSize={COUNTER_SIZES.LARGE}
                                  />
                                )}
                              </>
                            )}
                          </div>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ),
                  )}
                </SidebarMenu>
              </SidebarGroup>
            </React.Fragment>
          ))}
        </SidebarContent>
        <div
          className="sticky bottom-0 left-0 right-0 h-lg pointer-events-none"
          style={{
            background: "linear-gradient(to top, #F3F4F6, transparent)",
          }}
        />
      </div>
      <SidebarSeparator className="!my-0" />

      <SidebarFooter>
        <SidebarMenu>
          {footerRoutes.map((route) => (
            <SidebarMenuItem key={route.to}>
              <SidebarMenuButton
                to={route.to}
                icon={route.icon}
                activeIcon={route.activeIcon}
                label={route.label}
                className={`${route.className ?? ""} border-0 bg-transparent`}
                showTooltip
              />
            </SidebarMenuItem>
          ))}
          <AppVersion
            version={
              (
                import.meta as ImportMeta & {
                  env: { PACKAGE_VERSION: string };
                }
              ).env.PACKAGE_VERSION
            }
          />
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
};

export default Navigation;
